/**
 * Secondary dialogs and the calculate-field toolbar for the attribute table.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Button, Dialog, Field, Icon, IconButton, Stack, Text, tokens } from '@/design';
import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import { attributeInputStyle } from './attributeTableUi';
import type { SortLevel } from './attributeTableModel';
import { moveColumn } from './attributeTableModel';
import type { ColumnExploreStats } from './attributeTableStats';
import { numericColumnsForSummarize, type AggregateRequest } from './attributeTableSummarize';
import {
  defaultGeometryField,
  unitsForProperty,
  type GeometryProperty,
  type GeometryUnit,
} from './attributeGeometryCalc';

function SessionDialog({
  title,
  onClose,
  children,
  actions,
  size = 'md',
  testId,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly actions?: ReadonlyArray<{
    readonly label: string;
    readonly variant?: 'primary' | 'secondary' | 'ghost';
    readonly onClick: () => void;
    readonly defaultFocus?: boolean;
  }>;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly testId: string;
}) {
  if (typeof document === 'undefined') {
    return (
      <div role="dialog" aria-label={title} data-testid={testId}>
        <Text variant="subtitle" as="h2">{title}</Text>
        {children}
      </div>
    );
  }
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size={size}
      data-testid={testId}
      actions={actions ? [...actions] : undefined}
    >
      {children}
    </Dialog>
  );
}

export function AttributeCustomSortDialog({
  columns,
  initial,
  onApply,
  onClose,
}: {
  readonly columns: ReadonlyArray<string>;
  readonly initial: ReadonlyArray<SortLevel>;
  readonly onApply: (spec: SortLevel[]) => void;
  readonly onClose: () => void;
}) {
  const [levels, setLevels] = useState<SortLevel[]>(
    initial.length > 0
      ? [...initial].slice(0, 3)
      : [{ field: columns[0] ?? '', direction: 'asc' }],
  );

  const update = (index: number, patch: Partial<SortLevel>) => {
    setLevels((prev) => prev.map((level, i) => (i === index ? { ...level, ...patch } : level)));
  };

  return (
    <SessionDialog
      title="Custom sort"
      onClose={onClose}
      testId="attribute-custom-sort"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose, defaultFocus: true },
        {
          label: 'Apply',
          variant: 'primary',
          onClick: () => onApply(levels.filter((level) => level.field.length > 0)),
        },
      ]}
    >
      <Stack direction="column" gap={3}>
        {levels.map((level, index) => (
          <Stack key={`sort-${index}`} direction="row" gap={2} align="center">
            <Field label={`Sort ${index + 1}`}>
              {(props) => (
                <select
                  {...props}
                  value={level.field}
                  onChange={(event) => update(index, { field: event.target.value })}
                  style={attributeInputStyle()}
                >
                  {columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Direction">
              {(props) => (
                <select
                  {...props}
                  value={level.direction}
                  onChange={(event) => update(index, { direction: event.target.value as SortLevel['direction'] })}
                  style={attributeInputStyle()}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              )}
            </Field>
            {levels.length > 1 ? (
              <IconButton
                aria-label={`Remove sort level ${index + 1}`}
                onClick={() => setLevels((prev) => prev.filter((_, i) => i !== index))}
              >
                <Icon name="close" size={20} decorative />
              </IconButton>
            ) : null}
          </Stack>
        ))}
        {levels.length < 3 ? (
          <Button
            variant="secondary"
            onClick={() => setLevels((prev) => [...prev, { field: columns[0] ?? '', direction: 'asc' }])}
          >
            Add sort level
          </Button>
        ) : null}
      </Stack>
    </SessionDialog>
  );
}

export function AttributeFieldsDialog({
  order,
  hidden,
  onChange,
  onClose,
}: {
  readonly order: ReadonlyArray<string>;
  readonly hidden: ReadonlySet<string>;
  readonly onChange: (next: { order: string[]; hidden: Set<string> }) => void;
  readonly onClose: () => void;
}) {
  const [draftOrder, setDraftOrder] = useState<string[]>([...order]);
  const [draftHidden, setDraftHidden] = useState<Set<string>>(new Set(hidden));
  const visibleCount = draftOrder.filter((name) => !draftHidden.has(name)).length;

  return (
    <SessionDialog
      title="Fields"
      onClose={onClose}
      testId="attribute-fields"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose, defaultFocus: true },
        {
          label: 'Apply',
          variant: 'primary',
          onClick: () => onChange({ order: draftOrder, hidden: draftHidden }),
        },
      ]}
    >
      <Stack direction="column" gap={2}>
        {draftOrder.map((column, index) => {
          const checked = !draftHidden.has(column);
          return (
            <Stack key={column} direction="row" align="center" gap={2}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: tokens.space[2], flex: 1 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={checked && visibleCount <= 1}
                  onChange={() => {
                    setDraftHidden((prev) => {
                      const next = new Set(prev);
                      if (next.has(column)) next.delete(column);
                      else if (visibleCount > 1) next.add(column);
                      return next;
                    });
                  }}
                />
                <Text variant="body" as="span">{column}</Text>
              </label>
              <IconButton
                aria-label={`Move ${column} up`}
                disabled={index === 0}
                onClick={() => setDraftOrder((prev) => moveColumn(prev, column, -1))}
              >
                <Icon name="expand_less" size={20} decorative />
              </IconButton>
              <IconButton
                aria-label={`Move ${column} down`}
                disabled={index === draftOrder.length - 1}
                onClick={() => setDraftOrder((prev) => moveColumn(prev, column, 1))}
              >
                <Icon name="expand_more" size={20} decorative />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>
    </SessionDialog>
  );
}

export function AttributeExploreStatsDialog({
  stats,
  onClose,
}: {
  readonly stats: ColumnExploreStats;
  readonly onClose: () => void;
}) {
  return (
    <SessionDialog
      title={`Explore ${stats.column}`}
      onClose={onClose}
      testId="attribute-explore-stats"
      actions={[{ label: 'Close', variant: 'primary', onClick: onClose }]}
    >
      <Stack direction="column" gap={2}>
        <StatLine label="Count" value={stats.count} />
        <StatLine label="Nulls" value={stats.nulls} />
        <StatLine label="Unique" value={stats.uniqueCount} />
        {stats.kind === 'numeric' ? (
          <>
            <StatLine label="Min" value={formatStat(stats.min)} />
            <StatLine label="Max" value={formatStat(stats.max)} />
            <StatLine label="Mean" value={formatStat(stats.mean)} />
            <StatLine label="Median" value={formatStat(stats.median)} />
            <StatLine label="Std. dev." value={formatStat(stats.stddev)} />
          </>
        ) : (
          <StatLine
            label="Most frequent"
            value={stats.mostFrequent ? `${stats.mostFrequent.value} (${stats.mostFrequent.count})` : '—'}
          />
        )}
        {stats.frequencies.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: tokens.font.size.sm }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Value</th>
                <th style={{ textAlign: 'left' }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {stats.frequencies.map((row) => (
                <tr key={row.value}>
                  <td>{row.value}</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Stack>
    </SessionDialog>
  );
}

export function AttributeSummarizeDialog({
  columns,
  rows,
  initialField,
  onApply,
  onClose,
}: {
  readonly columns: ReadonlyArray<string>;
  readonly rows: ReadonlyArray<VectorExportFeature>;
  readonly initialField: string;
  readonly onApply: (groupBy: string, aggregates: AggregateRequest[]) => void;
  readonly onClose: () => void;
}) {
  const [groupBy, setGroupBy] = useState(initialField);
  const numeric = useMemo(
    () => numericColumnsForSummarize(rows, columns, groupBy),
    [rows, columns, groupBy],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fn, setFn] = useState<AggregateRequest['fn']>('sum');

  return (
    <SessionDialog
      title="Summarize"
      onClose={onClose}
      testId="attribute-summarize"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose, defaultFocus: true },
        {
          label: 'Summarize',
          variant: 'primary',
          onClick: () => onApply(
            groupBy,
            [...selected].map((field) => ({ field, fn })),
          ),
        },
      ]}
    >
      <Stack direction="column" gap={3}>
        <Field label="Group by">
          {(props) => (
            <select
              {...props}
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value)}
              style={attributeInputStyle()}
            >
              {columns.map((column) => (
                <option key={column} value={column}>{column}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Numeric statistic">
          {(props) => (
            <select
              {...props}
              value={fn}
              onChange={(event) => setFn(event.target.value as AggregateRequest['fn'])}
              style={attributeInputStyle()}
            >
              <option value="sum">Sum</option>
              <option value="mean">Mean</option>
              <option value="min">Min</option>
              <option value="max">Max</option>
            </select>
          )}
        </Field>
        <Text variant="caption" as="p" style={{ margin: 0 }}>
          Count is always included. Optionally add one statistic for numeric fields.
        </Text>
        {numeric.map((column) => (
          <label key={column} style={{ display: 'inline-flex', gap: tokens.space[2] }}>
            <input
              type="checkbox"
              checked={selected.has(column)}
              onChange={() => {
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (next.has(column)) next.delete(column);
                  else next.add(column);
                  return next;
                });
              }}
            />
            {column}
          </label>
        ))}
      </Stack>
    </SessionDialog>
  );
}

export function AttributeCalculateFieldDialog({
  columns,
  initialField,
  onApply,
  onClose,
}: {
  readonly columns: ReadonlyArray<string>;
  readonly initialField: string;
  readonly onApply: (targetField: string, expression: string) => string | null;
  readonly onClose: () => void;
}) {
  const [target, setTarget] = useState(initialField);
  const [expression, setExpression] = useState(initialField);
  const [error, setError] = useState<string | null>(null);

  return (
    <SessionDialog
      title="Calculate field"
      onClose={onClose}
      testId="attribute-calculate-field"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose, defaultFocus: true },
        {
          label: 'Apply',
          variant: 'primary',
          onClick: () => {
            const nextError = onApply(target, expression);
            setError(nextError);
          },
        },
      ]}
    >
      <CalculateFieldForm
        columns={columns}
        target={target}
        expression={expression}
        error={error}
        onTarget={setTarget}
        onExpression={setExpression}
      />
    </SessionDialog>
  );
}

export function AttributeCalculateGeometryDialog({
  initialField,
  onApply,
  onClose,
}: {
  readonly initialField: string;
  readonly onApply: (property: GeometryProperty, unit: GeometryUnit, outputField: string) => string | null;
  readonly onClose: () => void;
}) {
  const [property, setProperty] = useState<GeometryProperty>('area');
  const [unit, setUnit] = useState<GeometryUnit>('m2');
  const [output, setOutput] = useState(initialField || defaultGeometryField('area'));
  const [error, setError] = useState<string | null>(null);
  const units = unitsForProperty(property);

  return (
    <SessionDialog
      title="Calculate geometry"
      onClose={onClose}
      testId="attribute-calculate-geometry"
      actions={[
        { label: 'Cancel', variant: 'secondary', onClick: onClose, defaultFocus: true },
        {
          label: 'Apply',
          variant: 'primary',
          onClick: () => setError(onApply(property, unit, output)),
        },
      ]}
    >
      <Stack direction="column" gap={3}>
        <Field label="Property">
          {(props) => (
            <select
              {...props}
              value={property}
              onChange={(event) => {
                const next = event.target.value as GeometryProperty;
                setProperty(next);
                const nextUnits = unitsForProperty(next);
                setUnit(nextUnits[0] ?? 'm');
                setOutput(defaultGeometryField(next));
              }}
              style={attributeInputStyle()}
            >
              <option value="area">Area</option>
              <option value="length">Length</option>
              <option value="centroidX">Centroid X</option>
              <option value="centroidY">Centroid Y</option>
            </select>
          )}
        </Field>
        {units.length > 0 ? (
          <Field label="Unit">
            {(props) => (
              <select
                {...props}
                value={unit}
                onChange={(event) => setUnit(event.target.value as GeometryUnit)}
                style={attributeInputStyle()}
              >
                {units.map((value) => (
                  <option key={value} value={value}>{unitLabel(value)}</option>
                ))}
              </select>
            )}
          </Field>
        ) : null}
        <Field label="Output field" error={error ?? undefined}>
          {(props) => (
            <input
              {...props}
              value={output}
              onChange={(event) => setOutput(event.target.value)}
              style={attributeInputStyle()}
            />
          )}
        </Field>
      </Stack>
    </SessionDialog>
  );
}

export function AttributeCalculateToolbar({
  columns,
  initialField,
  onApply,
  onClose,
}: {
  readonly columns: ReadonlyArray<string>;
  readonly initialField: string;
  readonly onApply: (targetField: string, expression: string) => string | null;
  readonly onClose: () => void;
}) {
  const [target, setTarget] = useState(initialField);
  const [expression, setExpression] = useState(initialField);
  const [error, setError] = useState<string | null>(null);

  return (
    <div
      data-testid="attribute-calculate-toolbar"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        padding: tokens.space[2],
        background: tokens.color.surface[2],
        borderRadius: tokens.radius.md,
        border: tokens.border.thin,
      }}
    >
      <Stack direction="row" align="center" justify="space-between">
        <Text variant="caption" as="span">Calculate field</Text>
        <IconButton aria-label="Close calculate field toolbar" onClick={onClose}>
          <Icon name="close" size={20} decorative />
        </IconButton>
      </Stack>
      <CalculateFieldForm
        columns={columns}
        target={target}
        expression={expression}
        error={error}
        onTarget={setTarget}
        onExpression={setExpression}
      />
      <Button
        variant="primary"
        onClick={() => setError(onApply(target, expression))}
      >
        Apply
      </Button>
    </div>
  );
}

function CalculateFieldForm({
  columns,
  target,
  expression,
  error,
  onTarget,
  onExpression,
}: {
  readonly columns: ReadonlyArray<string>;
  readonly target: string;
  readonly expression: string;
  readonly error: string | null;
  readonly onTarget: (value: string) => void;
  readonly onExpression: (value: string) => void;
}) {
  return (
    <Stack direction="column" gap={2}>
      <Field label="Target field">
        {(props) => (
          <>
            <input
              {...props}
              list={`${props.id}-fields`}
              value={target}
              onChange={(event) => onTarget(event.target.value)}
              style={attributeInputStyle()}
            />
            <datalist id={`${props.id}-fields`}>
              {columns.map((column) => (
                <option key={column} value={column} />
              ))}
            </datalist>
          </>
        )}
      </Field>
      <Field label="Expression" error={error ?? undefined}>
        {(props) => (
          <input
            {...props}
            value={expression}
            onChange={(event) => onExpression(event.target.value)}
            placeholder="SHAPE_AREA / 10000"
            style={attributeInputStyle()}
          />
        )}
      </Field>
      <Text variant="caption" as="p" style={{ margin: 0 }}>
        Use field names, numbers, + − × ÷, ABS(), ROUND(), or CONCAT().
      </Text>
    </Stack>
  );
}

function StatLine({ label, value }: { readonly label: string; readonly value: string | number }) {
  return (
    <Stack direction="row" justify="space-between">
      <Text variant="caption" as="span">{label}</Text>
      <Text variant="body" as="span">{value}</Text>
    </Stack>
  );
}

function formatStat(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toPrecision(6);
}

function unitLabel(unit: GeometryUnit): string {
  if (unit === 'm') return 'Metres';
  if (unit === 'km') return 'Kilometres';
  if (unit === 'm2') return 'Square metres';
  if (unit === 'km2') return 'Square kilometres';
  return 'Hectares';
}
